// Contexto de gerenciamento de administradores
// Busca lista de admins do Firebase e expõe funções para adicionar/remover.
// Combina Super Admins (do env) com admins do Firestore.

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useDevLog } from '@/contexts/DevLogContext';
import { fetchAdmins, addAdmin as addAdminService, removeAdmin as removeAdminService } from '@/services/adminService';
import { isAdmin, isSuperAdmin, SUPER_ADMIN_EMAILS, getRawSuperAdminEnv } from '@/config/adminConfig';

const AdminContext = createContext();

// Flag para habilitar/desabilitar o log de diagnóstico de admin
const ENABLE_ADMIN_DEBUG_LOG = false;

export function AdminProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const { log: devLog } = useDevLog();
  const [adminList, setAdminList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoggedAdminDiagRef = React.useRef(false);

  // Buscar lista de admins do Firestore ao fazer login
  useEffect(() => {
    if (!isAuthenticated) {
      setAdminList([]);
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadAdmins = async () => {
      try {
        setLoading(true);
        setError(null);
        const admins = await fetchAdmins();
        if (mounted) {
          setAdminList(admins);
        }
      } catch (err) {
        console.error('Erro ao carregar admins:', err);
        if (mounted) {
          setError(err.message);
          // Mesmo com erro, permitir que Super Admins funcionem
          setAdminList([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadAdmins();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  // Verificar se usuário atual é admin
  const isCurrentUserAdmin = isAdmin(user?.email, adminList);

  // Verificar se usuário atual é super admin
  const isCurrentUserSuperAdmin = isSuperAdmin(user?.email);

  // Log de diagnóstico para entender por que o usuário não é reconhecido como admin (apenas 1 vez por sessão)
  useEffect(() => {
    if (!ENABLE_ADMIN_DEBUG_LOG) return;
    if (!user?.email) return;
    if (loading) return;
    if (hasLoggedAdminDiagRef.current) return;

    hasLoggedAdminDiagRef.current = true;
    devLog('[AdminContext] Diagnóstico de admin', {
      email: user.email,
      isCurrentUserAdmin,
      isCurrentUserSuperAdmin,
      superAdminEmails: SUPER_ADMIN_EMAILS,
      rawSuperAdminEnv: getRawSuperAdminEnv(),
      adminListFromFirestore: adminList,
      loading,
      error,
    });
  }, [user?.email, isCurrentUserAdmin, isCurrentUserSuperAdmin, adminList, loading, error, devLog]);

  // Adicionar novo admin
  const addAdmin = useCallback(
    async (email, displayName = '') => {
      if (!isCurrentUserAdmin) {
        throw new Error('Apenas admins podem adicionar outros admins');
      }

      try {
        await addAdminService(email, user.email, displayName);
        // Recarregar lista
        const admins = await fetchAdmins();
        setAdminList(admins);
      } catch (err) {
        console.error('Erro ao adicionar admin:', err);
        throw err;
      }
    },
    [isCurrentUserAdmin, user]
  );

  // Remover admin
  const removeAdmin = useCallback(
    async (email) => {
      if (!isCurrentUserAdmin) {
        throw new Error('Apenas admins podem remover outros admins');
      }

      // Não permitir remover super admins
      if (isSuperAdmin(email)) {
        throw new Error('Super Admins não podem ser removidos pela UI');
      }

      try {
        await removeAdminService(email);
        // Recarregar lista
        const admins = await fetchAdmins();
        setAdminList(admins);
      } catch (err) {
        console.error('Erro ao remover admin:', err);
        throw err;
      }
    },
    [isCurrentUserAdmin]
  );

  // Lista completa de admins (Super Admins + Firestore)
  const allAdmins = React.useMemo(() => {
    const superAdmins = SUPER_ADMIN_EMAILS.map((email) => ({
      email,
      isSuperAdmin: true,
      addedBy: 'env',
      addedAt: null,
    }));

    const firestoreAdmins = adminList.map((admin) => ({
      ...admin,
      isSuperAdmin: isSuperAdmin(admin.email),
    }));

    // Remover duplicatas (Super Admin que também está no Firestore)
    const emailSet = new Set();
    const combined = [];

    for (const admin of [...superAdmins, ...firestoreAdmins]) {
      if (!emailSet.has(admin.email)) {
        emailSet.add(admin.email);
        combined.push(admin);
      }
    }

    return combined;
  }, [adminList]);

  const value = {
    adminList: allAdmins,
    isCurrentUserAdmin,
    isCurrentUserSuperAdmin,
    loading,
    error,
    addAdmin,
    removeAdmin,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin deve ser usado dentro de AdminProvider');
  }
  return context;
}
